import type { DashboardAntwort } from '@symphenia/shared-types'
import { api } from '@/lib/axios'
import type { ExportRequest } from '@/lib/export-request'
import { mitStand } from '@/lib/speichern'
import type {
  AltUnitPayload,
  Article,
  ArticleDetail,
  ArticleDuplicate,
  ArticleGroup,
  ArticleImageUploadResult,
  ArticlePayload,
  ArticleSettings,
  ArticleSettingsPayload,
  ArticleSupplierRow,
  ArticleUnit,
  CustomerPrice,
  CustomerPricePayload,
  CustomerRef,
  ListArticlesParams,
  Paginated,
  SupplierRef,
  SupplierRowPayload,
} from '../types/article.types'

/** Ziele des Imports — dieselben Schlüssel wie `IMPORT_ZIELE` im Backend. */
export const IMPORT_TARGETS = [
  'articleNumber',
  'ean',
  'name',
  'documentText',
  'internalNote',
  'kind',
  'unit',
  'group',
  'salesPrice',
  'priceUnit',
  'weightPerUnit',
  'taxRate',
  'isSalesArticle',
  'isPurchaseArticle',
  'externalId',
  'externalSource',
  'supplierNumber',
  'supplierArticleNumber',
  'purchasePrice',
  'purchasePriceUnit',
] as const
export type ImportTarget = (typeof IMPORT_TARGETS)[number]
export const PURCHASE_TARGETS: readonly ImportTarget[] = ['supplierNumber', 'supplierArticleNumber', 'purchasePrice', 'purchasePriceUnit']

export interface ImportOptions {
  mapping?: Partial<Record<ImportTarget, string>>
  mode: 'create' | 'upsert'
  createMissingLists: boolean
}

export interface ImportMessage {
  code: string
  stufe: 'error' | 'info'
  feld?: ImportTarget
  wert?: string
}

export interface ImportRow {
  row: number
  action: 'create' | 'update' | 'skip' | 'error'
  articleNumber: string | null
  name: string | null
  messages: ImportMessage[]
}

export interface ImportPreview {
  headers: string[]
  sampleRows: string[][]
  mapping: Partial<Record<ImportTarget, string>>
  totalRows: number
  counts: { create: number; update: number; skip: number; error: number }
  unitsToCreate: string[]
  groupsToCreate: string[]
  rows: ImportRow[]
}

export interface ImportResult {
  created: number
  updated: number
  skipped: number
  errors: number
  unitsCreated: number
  groupsCreated: number
  rows: ImportRow[]
}

function importFormular(file: File, options: ImportOptions): FormData {
  const form = new FormData()
  form.append('file', file)
  if (options.mapping) form.append('mapping', JSON.stringify(options.mapping))
  form.append('mode', options.mode)
  form.append('createMissingLists', String(options.createMissingLists))
  return form
}

export const articlesApi = {
  /** AP-40 7.5 — alle Kacheln des Artikel-Dashboards in einem Aufruf. */
  dashboard: () => api.get<{ data: DashboardAntwort }>('/articles/dashboard').then((r) => r.data.data),
  list: (params?: ListArticlesParams) => api.get<Paginated<Article>>('/articles', { params }),

  get: (id: string) => api.get<ArticleDetail>(`/articles/${id}`),

  /** `allowDuplicate`: trotz gleicher Bezeichnung anlegen (N-21) — eine doppelte EAN bleibt gesperrt. */
  create: (payload: ArticlePayload & { name: string; unitId: string; allowDuplicate?: boolean }) =>
    api.post<ArticleDetail>('/articles', payload),

  /** Gibt es den schon? — gleiche Bezeichnung (normalisiert) oder EAN, höchstens zehn. */
  duplicates: (params: { name?: string; ean?: string }) =>
    api.get<{ data: ArticleDuplicate[] }>('/articles/duplicates', { params }),

  /**
   * `stand` reist im Header `X-Stand` (AP-36 8.1): Der Server lehnt einen
   * überholten Stand mit 409 ab, damit feldweises Speichern nichts überschreibt.
   */
  update: (id: string, payload: ArticlePayload, stand?: string) =>
    api.patch<ArticleDetail>(`/articles/${id}`, payload, mitStand(stand)),

  /** Deaktivieren ist der Regelfall — umkehrbar. */
  setActive: (id: string, isActive: boolean) =>
    api.patch<ArticleDetail>(`/articles/${id}/active`, { isActive }),

  /** Endgültig löschen — `409`, solange Lieferanten hinterlegt sind. */
  purge: (id: string) => api.delete<void>(`/articles/${id}`),

  // ── Export (AP-28 7.7) — als Anfrage für den ExportButton (AP-10 Nachtrag 5) ──
  exportRequest: (
    params: ListArticlesParams & {
      format: 'xlsx' | 'docx' | 'pdf'
      orientation?: 'portrait' | 'landscape'
      columns: { key: string; label: string }[]
      filterInfo?: string
    },
  ): ExportRequest => {
    const { columns, ...rest } = params
    return { url: '/articles/export', params: { ...rest, columns: JSON.stringify(columns), locale: navigator.language } }
  },

  // ── Import (AP-28 8) — dieselbe Datei für Vorschau und Übernahme ──
  importPreview: (file: File, options: ImportOptions) =>
    api.post<ImportPreview>('/articles/import/preview', importFormular(file, options), {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  importCommit: (file: File, options: ImportOptions) =>
    api.post<ImportResult>('/articles/import/commit', importFormular(file, options), {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // ── Einstellungen (Nachtrag N-1) — lesen mit article.read, schreiben mit settings.manage ──
  settings: () => api.get<{ data: ArticleSettings }>('/articles/settings'),
  updateSettings: (payload: ArticleSettingsPayload) =>
    api.patch<{ data: ArticleSettings }>('/articles/settings', payload),

  // ── Alternativeinheiten (N-5) — die ganze Liste auf einmal ──
  //
  // Die Liste hat keinen eigenen Stand: Sie wird ersetzt, und maßgeblich ist der
  // **Artikel**, den der Server in derselben Transaktion anfasst (AP-36 8.3 a).
  replaceAltUnits: (id: string, altUnits: AltUnitPayload[], stand?: string) =>
    api.put<ArticleDetail>(`/articles/${id}/alt-units`, { altUnits }, mitStand(stand)),

  // ── Kundenpreise (N-8) ──
  customerPrices: (id: string) => api.get<{ data: CustomerPrice[] }>(`/articles/${id}/customer-prices`),
  addCustomerPrice: (id: string, payload: CustomerPricePayload & { customerId: string; price: number }) =>
    api.post<CustomerPrice>(`/articles/${id}/customer-prices`, payload),
  /** Stand je Zeile (`X-Stand`) — jede Preiszeile ist eine eigene Speicherstelle. */
  updateCustomerPrice: (id: string, rowId: string, payload: Omit<CustomerPricePayload, 'customerId'>, stand?: string) =>
    api.patch<CustomerPrice>(`/articles/${id}/customer-prices/${rowId}`, payload, mitStand(stand)),
  deleteCustomerPrice: (id: string, rowId: string) =>
    api.delete<{ message: string }>(`/articles/${id}/customer-prices/${rowId}`),

  // ── Bild (AP-28 4.4) ──
  uploadImage: (id: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api.post<ArticleImageUploadResult>(`/articles/${id}/image`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  deleteImage: (id: string) => api.delete<ArticleDetail>(`/articles/${id}/image`),

  // ── Lieferantenzeilen (nur mit articles.purchase.view) ──
  addSupplier: (id: string, payload: SupplierRowPayload) =>
    api.post<ArticleSupplierRow>(`/articles/${id}/suppliers`, payload),
  /** Stand je Zeile (`X-Stand`) — jede Lieferantenzeile ist eine eigene Speicherstelle. */
  updateSupplier: (id: string, rowId: string, payload: SupplierRowPayload, stand?: string) =>
    api.patch<ArticleSupplierRow>(`/articles/${id}/suppliers/${rowId}`, payload, mitStand(stand)),
  deleteSupplier: (id: string, rowId: string) => api.delete(`/articles/${id}/suppliers/${rowId}`),

  // ── Stammlisten ──
  groups: () => api.get<{ data: ArticleGroup[] }>('/articles/groups'),
  createGroup: (payload: { name: string; sortOrder?: number }) =>
    api.post<ArticleGroup>('/articles/groups', payload),
  updateGroup: (id: string, payload: Partial<Pick<ArticleGroup, 'name' | 'sortOrder' | 'isActive'>>) =>
    api.patch<ArticleGroup>(`/articles/groups/${id}`, payload),
  deleteGroup: (id: string) => api.delete(`/articles/groups/${id}`),

  units: () => api.get<{ data: ArticleUnit[] }>('/articles/units'),
  createUnit: (payload: { code: string; name: string; decimals?: number; sortOrder?: number }) =>
    api.post<ArticleUnit>('/articles/units', payload),
  updateUnit: (
    id: string,
    payload: Partial<Pick<ArticleUnit, 'code' | 'name' | 'decimals' | 'sortOrder' | 'isActive'>>,
  ) => api.patch<ArticleUnit>(`/articles/units/${id}`, payload),
  deleteUnit: (id: string) => api.delete(`/articles/units/${id}`),
  seedUnits: () => api.post<{ data: ArticleUnit[] }>('/articles/units/seed-defaults'),

  /**
   * Geschäftspartner mit Lieferantenrolle — über den **Endpunkt** des CRM, nicht
   * über dessen Frontend-Interna (Modulgrenzen). Braucht `crm.client.read`.
   */
  suppliers: (search?: string) =>
    api.get<Paginated<SupplierRef & { clientNumber: string }>>('/clients', {
      params: { role: 'supplier', isActive: true, limit: 100, sortBy: 'name', sortDir: 'asc', search },
    }),

  /** Geschäftspartner mit Kundenrolle — für die Kundenpreise, gleicher Weg wie `suppliers`. */
  customers: (search?: string) =>
    api.get<Paginated<CustomerRef>>('/clients', {
      params: { role: 'customer', isActive: true, limit: 100, sortBy: 'name', sortDir: 'asc', search },
    }),
}
